/**
 * My Application Skills — auto-évaluation des fonctionnalités applicatives.
 * Indépendant des assessments BPML : scores partagés sur toute l'année.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  featureScoresApi,
  configApi,
  ApiRequestError,
  type FeatureScoreDto,
  type ApplicationDto,
} from "../api/api";
import { useAuth } from "../context/AuthContext";
import { localeLabel } from "../utils/locale";

// ── Types ─────────────────────────────────────────────────────────────────────

type ScaleLevel = { level: number; labelEn: string; labelFr: string };

const DEFAULT_SCALE: ScaleLevel[] = [
  { level: 0, labelEn: "None", labelFr: "Aucun" },
  { level: 1, labelEn: "Basic", labelFr: "Basique" },
  { level: 2, labelEn: "Intermediate", labelFr: "Intermédiaire" },
  { level: 3, labelEn: "Advanced", labelFr: "Avancé" },
  { level: 4, labelEn: "Expert", labelFr: "Expert" },
];

const SCORE_CLS = ["sm-score-0", "sm-score-1", "sm-score-2", "sm-score-3", "sm-score-4"] as const;

function scoreClass(s: number | null): string {
  if (s === null || s < 0 || s > 4) return "";
  return SCORE_CLS[s];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type ModuleGroup = {
  moduleCode: string | null;
  moduleLabel: string | null;
  subModules: SubModuleGroup[];
};
type SubModuleGroup = {
  subModuleCode: string | null;
  subModuleLabel: string | null;
  features: FeatureScoreDto[];
};

function groupFeatures(features: FeatureScoreDto[]): ModuleGroup[] {
  const mods = new Map<string, ModuleGroup>();
  for (const f of features) {
    const mk = f.moduleCode ?? "__none__";
    if (!mods.has(mk)) mods.set(mk, { moduleCode: f.moduleCode, moduleLabel: f.moduleLabel, subModules: [] });
    const mod = mods.get(mk)!;
    const sk = f.subModuleCode ?? "__none__";
    let sub = mod.subModules.find((s) => (s.subModuleCode ?? "__none__") === sk);
    if (!sub) {
      sub = { subModuleCode: f.subModuleCode, subModuleLabel: f.subModuleLabel, features: [] };
      mod.subModules.push(sub);
    }
    sub.features.push(f);
  }
  return Array.from(mods.values());
}

// ── Score pill ────────────────────────────────────────────────────────────────

function ScorePill({
  score,
  onClick,
  readOnly,
}: {
  score: number | null;
  onClick: (s: number | null) => void;
  readOnly?: boolean;
}) {
  const cls = score !== null ? SCORE_CLS[score] : "sm-score-null";
  if (readOnly) {
    return <span className={`sm-score-pill ${cls}`}>{score !== null ? score : "·"}</span>;
  }
  return (
    <span className={`sm-score-pill ${cls}`} style={{ cursor: "pointer" }} onClick={() => onClick(score)}>
      {score !== null ? score : "·"}
    </span>
  );
}

// ── Feature row ───────────────────────────────────────────────────────────────

function FeatureRow({
  feature,
  score,
  comment,
  scale,
  onChange,
}: {
  feature: FeatureScoreDto;
  score: number | null;
  comment: string;
  scale: ScaleLevel[];
  onChange: (featureId: string, score: number | null, comment?: string) => void;
}) {
  const { i18n } = useTranslation();
  const langFr = i18n.language?.toLowerCase().startsWith("fr");
  const label = langFr && feature.featureLabelFr ? feature.featureLabelFr : feature.featureLabelEn;
  const [showComment, setShowComment] = useState(false);

  const scaleLabel = score !== null
    ? (langFr ? scale.find(s => s.level === score)?.labelFr : scale.find(s => s.level === score)?.labelEn) ?? String(score)
    : null;

  return (
    <div className="sm-assess-line" style={{ paddingLeft: 0 }}>
      <div className="sm-assess-line-label">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontFamily: "monospace", fontSize: "0.8em", fontWeight: 600,
            background: "#f1f5f9", padding: "1px 6px", borderRadius: 4, color: "#475569",
          }}>
            {feature.featureCode}
          </span>
          <span>{label}</span>
        </div>
      </div>
      <div className="sm-assess-scores">
        {scale.map((s) => (
          <button
            key={s.level}
            type="button"
            title={langFr ? s.labelFr : s.labelEn}
            className={`sm-score-btn${score === s.level ? ` ${scoreClass(s.level)}` : ""}`}
            onClick={() => onChange(feature.featureId, score === s.level ? null : s.level)}
          >
            {s.level}
          </button>
        ))}
        <button
          type="button"
          className="sm-link"
          style={{ fontSize: "0.78em", marginLeft: 4, color: "#64748b" }}
          onClick={() => setShowComment((v) => !v)}
        >
          💬
        </button>
      </div>
      {showComment ? (
        <div style={{ marginTop: 6, gridColumn: "1 / -1" }}>
          <textarea
            className="sm-input"
            rows={2}
            style={{ width: "100%", fontSize: "0.85em" }}
            placeholder="Comment…"
            value={comment}
            onChange={(e) => onChange(feature.featureId, score, e.target.value)}
          />
        </div>
      ) : null}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export function MyApplicationSkillsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const langFr = i18n.language?.toLowerCase().startsWith("fr");

  const [year, setYear] = useState(new Date().getFullYear());
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string>("");
  const [features, setFeatures] = useState<FeatureScoreDto[]>([]);
  const [scale, setScale] = useState<ScaleLevel[]>(DEFAULT_SCALE);
  const [scores, setScores] = useState<Record<string, { score: number | null; comment: string }>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lot 3 — Bulk-apply (« prérenseigner toutes les features vides »)
  const [bulkScore, setBulkScore] = useState<number>(0);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);

  const scoresRef = useRef(scores);
  scoresRef.current = scores;
  // Track the year at the time edits are queued so flushSave always saves to the right year
  // even when the user switches year/app before the debounce fires.
  const flushYearRef = useRef(year);
  const dirtyRef = useRef(new Set<string>());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  // Charger les applications + scale au montage
  useEffect(() => {
    void assessmentApi.listErp().then((r) => setApplications(r.applications.filter((a) => a.active)));
    void configApi.listScale().then((r) => {
      if (r.scale.length > 0) setScale(r.scale.map(s => ({ level: s.level, labelEn: s.labelEn, labelFr: s.labelFr })));
    });
  }, []);

  // Auto-save debounced.
  // Declared BEFORE load/useEffect because both reference it.
  // Uses flushYearRef (not reactive `year`) so edits are always saved against the year
  // that was active when the user made them, even if year/app changes before the timer fires.
  const flushSave = useCallback(async () => {
    const dirty = Array.from(dirtyRef.current);
    if (dirty.length === 0) { setSaved(true); return; }
    const current = scoresRef.current;
    const saveYear = flushYearRef.current;
    const lines = dirty.map((featureId) => {
      const v = current[featureId] ?? { score: null, comment: "" };
      return {
        featureId,
        score: v.score,
        comment: v.comment.trim() === "" ? null : v.comment,
      };
    });
    setBusy(true);
    try {
      await featureScoresApi.patch(saveYear, lines);
      dirtyRef.current = new Set();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("save_failed"));
    } finally {
      setBusy(false);
    }
  }, [t]); // no `year` dep — reads flushYearRef at call time

  // Charger les scores quand app ou année change
  const load = useCallback(async () => {
    // Nouvelle sélection (app/année) : on efface les messages liés à l'ancien contexte
    // (erreur de patch/bulk-apply précédente, message de succès bulk-apply précédent),
    // sinon ils resteraient affichés par erreur sur un contexte différent.
    setError(null);
    setBulkMessage(null);
    setBulkConfirming(false);
    if (!selectedAppId) { setFeatures([]); setScores({}); return; }
    try {
      const res = await featureScoresApi.get(year, selectedAppId);
      setFeatures(res.scores);
      const map: Record<string, { score: number | null; comment: string }> = {};
      for (const f of res.scores) {
        map[f.featureId] = { score: f.score, comment: f.comment ?? "" };
      }
      setScores(map);
      dirtyRef.current = new Set();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("load_failed"));
    }
  }, [year, selectedAppId, t]);

  useEffect(() => {
    // Cancel any pending debounce and flush edits for the *old* year/app before loading new data.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    void flushSave().then(() => load());
    // Cleanup on unmount: cancel pending timer (flush was already triggered above).
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [load, flushSave]);

  function onChange(featureId: string, score: number | null, comment?: string) {
    // Capture the active year so flushSave sends to the correct year even if the user
    // changes the year selector before the debounce fires.
    flushYearRef.current = year;
    setScores((prev) => {
      const cur = prev[featureId] ?? { score: null, comment: "" };
      return {
        ...prev,
        [featureId]: {
          // `score` is always explicitly provided (number | null); do NOT coalesce with
          // cur.score — that would make it impossible to clear a score (toggle off → null).
          score: score,
          comment: comment !== undefined ? comment : cur.comment,
        },
      };
    });
    dirtyRef.current.add(featureId);
    setSaved(false);
    // Un édit individuel rend l'ancien message de succès bulk-apply obsolète.
    setBulkMessage(null);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { void flushSave(); }, 1200);
  }

  // Lot 3 — Bulk-apply : ne remplit que les features actives actuellement vides
  // (pas de ligne ou score NULL) de l'application sélectionnée. Ne touche jamais
  // une feature déjà notée individuellement.
  const emptyFeatureCount = features.filter(
    (f) => (scores[f.featureId]?.score ?? null) === null
  ).length;

  async function runBulkApply() {
    if (!selectedAppId) return;
    // On flush d'abord les éventuelles saisies individuelles en attente, pour que le
    // serveur connaisse le dernier état "vide / notée" avant d'appliquer le bulk.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    await flushSave();
    setBulkConfirming(false);
    setBulkBusy(true);
    setError(null);
    setBulkMessage(null);
    try {
      const res = await featureScoresApi.bulkApply({
        applicationId: selectedAppId,
        year,
        score: bulkScore,
      });
      // `load()` remet `bulkMessage` à null (nouveau contexte) : on affiche donc le
      // message de succès APRÈS le rechargement, pas avant.
      await load();
      setBulkMessage(
        res.appliedCount > 0
          ? t("bulk_apply_success", { count: res.appliedCount })
          : t("bulk_apply_success_none")
      );
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("bulk_apply_failed"));
    } finally {
      setBulkBusy(false);
    }
  }

  // Stats
  const total = features.length;
  const scored = features.filter((f) => (scores[f.featureId]?.score ?? null) !== null).length;
  const pct = total > 0 ? Math.round((scored / total) * 100) : 0;

  const groups = groupFeatures(features);

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("my_application_skills_title")}</h1>
          <p className="sm-muted">{t("my_application_skills_sub")}</p>
        </div>
        {!saved && (
          <span style={{ fontSize: "0.85em", color: "#f59e0b", fontWeight: 500 }}>
            {busy ? t("saving") : t("unsaved_changes")}
          </span>
        )}
        {saved && total > 0 && (
          <span style={{ fontSize: "0.85em", color: "#22c55e", fontWeight: 500 }}>✓ {t("saved")}</span>
        )}
      </div>

      {error && <div className="sm-alert sm-alert-error">{error}</div>}
      {bulkMessage && <div className="sm-alert sm-alert-ok">{bulkMessage}</div>}

      {/* Sélecteurs */}
      <div className="sm-card sm-card-pad" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <label className="sm-label">
          {t("year")}
          <select className="sm-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="sm-label">
          {t("application")}
          <select
            className="sm-select"
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
          >
            <option value="">— {t("select_application")} —</option>
            {applications.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Barre de progression */}
      {selectedAppId && total > 0 && (
        <div className="sm-card sm-card-pad" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "0.85em" }}>
            <span>{t("progress")}</span>
            <span>{scored}/{total} ({pct}%)</span>
          </div>
          <div style={{ height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "#2563eb", borderRadius: 4, transition: "width .3s" }} />
          </div>
        </div>
      )}

      {/* Lot 3 — Bulk-apply : prérenseigner toutes les features vides de l'application */}
      {selectedAppId && total > 0 && (
        <div className="sm-card sm-card-pad" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <strong style={{ fontSize: "0.9em" }}>{t("bulk_apply_title")}</strong>
            <label className="sm-label" style={{ margin: 0 }}>
              {t("bulk_apply_score_label")}
              <select
                className="sm-select"
                style={{ marginLeft: 8 }}
                value={bulkScore}
                disabled={bulkBusy || emptyFeatureCount === 0}
                onChange={(e) => setBulkScore(Number(e.target.value))}
              >
                {scale.map((s) => (
                  <option key={s.level} value={s.level}>
                    {s.level} — {langFr ? s.labelFr : s.labelEn}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="sm-btn"
              disabled={bulkBusy || emptyFeatureCount === 0}
              onClick={() => setBulkConfirming(true)}
            >
              {t("bulk_apply_button")} ({emptyFeatureCount})
            </button>
          </div>

          {emptyFeatureCount === 0 && (
            <p className="sm-muted" style={{ marginTop: 8, marginBottom: 0, fontSize: "0.85em" }}>
              {t("bulk_apply_none_empty")}
            </p>
          )}

          {bulkConfirming && (
            <div
              className="sm-alert sm-alert-info"
              style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
            >
              <span>{t("bulk_apply_confirm", { score: bulkScore })}</span>
              <div className="sm-form-actions" style={{ margin: 0 }}>
                <button
                  type="button"
                  className="sm-btn sm-btn-primary"
                  disabled={bulkBusy}
                  onClick={() => void runBulkApply()}
                >
                  {bulkBusy ? t("saving") : t("bulk_apply_button")}
                </button>
                <button
                  type="button"
                  className="sm-btn"
                  disabled={bulkBusy}
                  onClick={() => setBulkConfirming(false)}
                >
                  {t("bulk_apply_cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pas d'application sélectionnée */}
      {!selectedAppId && (
        <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
          {t("select_application_hint")}
        </div>
      )}

      {/* Pas de features */}
      {selectedAppId && total === 0 && (
        <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
          {t("no_features_for_application")}
        </div>
      )}

      {/* Features groupées par Module → Sub-module */}
      {groups.map((mod) => (
        <div key={mod.moduleCode ?? "__none__"} className="sm-card" style={{ marginBottom: 12 }}>
          {mod.moduleCode && (
            <div style={{
              background: "#1e40af", color: "#fff",
              padding: "10px 16px", fontWeight: 600, fontSize: "0.95em", borderRadius: "6px 6px 0 0",
            }}>
              {mod.moduleCode} — {mod.moduleLabel ?? mod.moduleCode}
            </div>
          )}
          {mod.subModules.map((sub) => (
            <div key={sub.subModuleCode ?? "__none__"} style={{ padding: "0 16px 12px" }}>
              {sub.subModuleCode && (
                <div style={{
                  padding: "8px 0 4px", fontWeight: 600, fontSize: "0.85em",
                  color: "#475569", borderBottom: "1px solid #e2e8f0", marginBottom: 4,
                }}>
                  {sub.subModuleCode} — {sub.subModuleLabel ?? sub.subModuleCode}
                </div>
              )}
              {sub.features.map((f) => (
                <FeatureRow
                  key={f.featureId}
                  feature={f}
                  score={scores[f.featureId]?.score ?? null}
                  comment={scores[f.featureId]?.comment ?? ""}
                  scale={scale}
                  onChange={onChange}
                />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
